/**
 * Cambio centralizado de empresa activa.
 *
 * Fuente única de verdad para los 3 puntos donde el usuario puede cambiar la empresa:
 *  - Selector del Sidebar
 *  - Selector "Cambiar de empresa" en CompanyInformationView
 *  - Tarjetas en Lista de empresas (CompaniesSettings)
 *
 * Garantiza, en orden y SÍNCRONAMENTE (antes de devolver):
 *  1) localStorage actualizado (active_company + isActive en user_companies)
 *  2) authStore.business actualizado con name, logo, banner, appearance
 *  3) Reset del estado dependiente de la empresa (availability + stores con scope)
 *  4) triggerCompanyChange() para notificar a watchers globales (App.vue, Sidebar)
 *
 * Mantener esto en un solo lugar evita el desync entre el header del Sidebar y los
 * dropdowns de cambio (multi-tenant: empresa anterior se "pega" al header).
 */
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useUsersStore } from '@/stores/users'
import { useClientsStore } from '@/stores/clients'
import { useAppointmentsStore } from '@/stores/appointments'
import { useServicesStore } from '@/stores/services'
import { useNotificationsStore } from '@/stores/notifications'
import { useSupportTicketsStore } from '@/stores/supportTickets'
import { companiesService } from '@/services/CompaniesService'

export function useCompanySwitcher() {
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const usersStore = useUsersStore()
  const clientsStore = useClientsStore()
  const appointmentsStore = useAppointmentsStore()
  const servicesStore = useServicesStore()
  const notificationsStore = useNotificationsStore()
  const supportTicketsStore = useSupportTicketsStore()

  function buildNormalizedCompany(company, currentActive) {
    const currentBusiness = authStore.business || {}
    return {
      id: company.id,
      tenant_id: company.tenant_id ?? null,
      name: company.name ?? currentActive?.name ?? currentBusiness.name ?? '',
      email: company.email ?? currentBusiness.email ?? null,
      phone: company.phone ?? currentBusiness.phone ?? null,
      logo: company.logo ?? company.logoCompany ?? company.logo_company ?? null,
      logoCompany:
        company.logoCompany ?? company.logo_company ?? company.logo ?? null,
      bannerCompany:
        company.bannerCompany ?? company.banner_company ?? null,
      appearance: company.appearance ?? null,
      // El rol es por empresa: nunca heredar del business/empresa anterior
      role: company.role ?? null,
      role_id: company.role_id ?? null,
      hierarchy_level: company.hierarchy_level ?? null,
      active: company.active !== false
    }
  }

  /**
   * Cambia la empresa activa.
   * @param {Object} company Empresa a activar. Mínimo { id, name }; idealmente con tenant_id.
   * @param {Object} [options]
   * @param {boolean} [options.resetScopedStores=true] Si false, no resetea users/clients/appointments/services stores.
   * @returns {{ switched: boolean, company: Object|null }}
   */
  function switchToCompany(company, options = {}) {
    if (!company || (company.id == null && company.tenant_id == null)) {
      return { switched: false, company: null }
    }

    const current = companiesService.getActiveCompany()
    if (current && companiesService.isSameCompany(current, company)) {
      return { switched: false, company: current }
    }

    const normalized = buildNormalizedCompany(company, current)

    companiesService.setActiveCompany(normalized)

    const stored = companiesService.getCompaniesFromLocal() || []
    const updatedList = stored.map((c) => ({
      ...c,
      isActive: String(c.id) === String(normalized.id)
    }))
    companiesService.saveCompaniesToLocal(updatedList)

    authStore.updateBusiness({
      id: normalized.id,
      tenant_id: normalized.tenant_id,
      name: normalized.name,
      email: normalized.email,
      phone: normalized.phone,
      logo: normalized.logo,
      logoCompany: normalized.logoCompany,
      bannerCompany: normalized.bannerCompany,
      ...(normalized.appearance ? { appearance: normalized.appearance } : {})
    })

    appStore.resetAvailabilityState()

    // notificationsStore es composition-style: no tiene $reset nativo (eso es solo
    // Options API), por eso se llama su reset() explícito para no filtrar notificaciones
    // del tenant anterior. App.vue se encarga de re-pedir los datos del tenant nuevo.
    notificationsStore.reset()

    if (options.resetScopedStores !== false) {
      usersStore.$reset?.()
      clientsStore.$reset?.()
      appointmentsStore.$reset?.()
      servicesStore.$reset?.()
      supportTicketsStore.$reset?.()
    }

    appStore.triggerCompanyChange()

    // Feedback no bloqueante: el cambio ya es visible en pantalla (header del Sidebar,
    // ENTORNO ACTUAL, badge Activa). Un toast confirma la acción sin interrumpir el flujo;
    // un modal sería ruido en una operación que se repite muchas veces por sesión.
    if (options.silent !== true) {
      appStore.addSuccess?.(`Empresa activa: ${normalized.name || 'sin nombre'}`)
    }

    return { switched: true, company: normalized }
  }

  return { switchToCompany }
}
